import { Linkedin, Instagram, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navCols = [
  {
    title: "Company",
    links: [
      { label: "Home", to: "/" },
      { label: "About", to: "/about" },
      { label: "Blog", to: "/blog" },
      { label: "Case Studies", to: "/case-studies" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Brand", to: "/services" },
      { label: "Growth", to: "/services" },
      { label: "Automation", to: "/services" },
      { label: "Industries", to: "/industries" },
    ],
  },
  {
    title: "Products",
    links: [
      { label: "AI Lead Gen", to: "/products" },
      { label: "Marketing SaaS", to: "/products" },
      { label: "Real Estate Platform", to: "/products" },
      { label: "AI Lab", to: "/ai-lab" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email first."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Growth field notes incoming.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't subscribe right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative border-t border-white/[0.06] mt-8">
      <div className="container-x py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="relative w-8 h-8 rounded-lg overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-tr from-neon-magenta via-neon-orange to-neon-cyan" />
                <span className="absolute inset-[2px] rounded-md bg-bg-0 flex items-center justify-center font-mono font-extrabold text-[12px]">NW</span>
              </span>
              <span className="font-semibold tracking-tight">Novarise <span className="text-white/50">Works</span></span>
            </Link>
            <p className="mt-4 text-sm text-white/45 max-w-sm leading-relaxed">
              Growth infrastructure for ambitious businesses. Brand × Growth × Automation, engineered together.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2 max-w-sm" data-testid="footer-newsletter">
              <input
                data-testid="footer-newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Get our growth notes"
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-neon-cyan/40 focus:bg-white/[0.07]"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="footer-newsletter-submit"
                className="px-3 rounded-lg bg-white text-bg-0 font-semibold text-sm hover:shadow-[0_0_30px_rgba(0,240,255,0.35)] transition disabled:opacity-60"
              >
                <ArrowRight size={15} />
              </button>
            </form>
          </div>

          {navCols.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="overline mb-4">{col.title}</p>
              <ul className="space-y-2.5 text-sm text-white/65">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link className="hover:text-white transition-colors" to={l.to}>{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-2">
            <p className="overline mb-4">Connect</p>
            <a href="mailto:hello@novariseworks.com" className="block text-sm font-semibold hover:text-neon-cyan transition-colors">
              hello@novariseworks.com
            </a>
            <div className="mt-4 flex items-center gap-2">
              {[
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/novariseworks/" },
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/novariseworks?igsh=MWN6Z29teTVxdDZ5YQ==" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:border-white/30 transition"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40 font-mono">
          <span>© {new Date().getFullYear()} Novarise Works · novariseworks.com</span>
          <span>Built to compound.</span>
        </div>
      </div>
    </footer>
  );
}
